import { ORDER_STATUS } from '../types/orders';
import { createEvent, IBaseHistoryEvent } from '../utils/history';
import {
  LOGISTIC_SERVICE_STATUS_NAME,
  TLogisticServiceStatusName,
  OLD_ORDER_STATUS_ID,
  TOldOrderStatusId
} from '../types/suscription/history.types';

const SALES_STATUSES_AS_ACTIVE_ORDER = [
  OLD_ORDER_STATUS_ID.ACTIVE,
  OLD_ORDER_STATUS_ID.CONFIRMED,
  OLD_ORDER_STATUS_ID.DELIVERY_PROCESS
];

/**
 * Determina el estado de la orden calculado a partir del estado antiguo
 * @param oldOrderStatusId - Estado de la orden antiguo
 * @returns Nuevo estado de la orden
 */
export const determineNewOrderStatus = (oldOrderStatusId: TOldOrderStatusId): ORDER_STATUS | null => {
  if (oldOrderStatusId == OLD_ORDER_STATUS_ID.PAYMENT_PROCESS) {
    return ORDER_STATUS.PENDING;
  }
  if (SALES_STATUSES_AS_ACTIVE_ORDER.includes(oldOrderStatusId)) {
    return ORDER_STATUS.ACTIVE;
  }
  if (oldOrderStatusId == OLD_ORDER_STATUS_ID.DELIVERED) {
    return ORDER_STATUS.DELIVERED;
  }
  if (oldOrderStatusId == OLD_ORDER_STATUS_ID.CANCELLED) {
    return ORDER_STATUS.CANCELLED;
  }
  return null;
};

/**
 * Array ordenado de estados en secuencia de progreso para el historial de la orden
 */
export const ORDER_STATUS_SEQUENCE: ORDER_STATUS[] = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.ACTIVE,
  ORDER_STATUS.DELIVERED
];

/**
 * Array ordenado de estados en secuencia de progreso para el historial del servicio logístico
 */
export const LOGISTIC_SERVICE_STATUS_SEQUENCE: TLogisticServiceStatusName[] = [
  LOGISTIC_SERVICE_STATUS_NAME.TO_CONFIRMED,
  LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED,
  LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS,
  LOGISTIC_SERVICE_STATUS_NAME.ACTIVE,
  LOGISTIC_SERVICE_STATUS_NAME.DELIVERED
];

/**
 * Nombres de las fechas de la orden
 */
const ORDER_DATES = {
  CREATED_AT: 'createdAt',
  PAYMENT_DATE: 'paymentDate',
  ACTIVATION_DATE: 'activationDate',
  DELIVERY_DATE: 'deliveryDate',
  CANCELLED_DATE: 'cancelledDate'
} as const;
export type TOrderDates = typeof ORDER_DATES[keyof typeof ORDER_DATES];

/**
 * Nombres de las fechas del servicio logístico
 */
const LOGISTIC_SERVICE_DATES = {
  REQUEST_DATE: 'requestDate',
  CONFIRMED_DATE: 'confirmedDate',
  IN_PROCESS_DATE: 'inProcessDate',
  ACTIVATION_DATE: 'activationDate',
  DELIVERED_DATE: 'deliveryDate',
  CANCELLED_DATE: 'cancelledDate'
} as const;
export type TLogisticServiceDates = typeof LOGISTIC_SERVICE_DATES[keyof typeof LOGISTIC_SERVICE_DATES];

interface IHistoryBaseEvents<T> {
  [key: string]: {
    statusName: T;
    events: readonly IBaseHistoryEvent[];
  };
}

type TOrderHistoryBaseEvents = IHistoryBaseEvents<ORDER_STATUS>;
type TLogisticServiceHistoryBaseEvents = IHistoryBaseEvents<TLogisticServiceStatusName>;

/**
 * Eventos base de la orden
 */
export const ORDER_HISTORY_BASE_EVENTS: TOrderHistoryBaseEvents = {
  [ORDER_DATES.CREATED_AT]: {
    statusName: ORDER_STATUS.PENDING,
    events: [createEvent('Orden creada', 'Se crea la orden he ingresa al estado Por confirmar')]
  },
  [ORDER_DATES.ACTIVATION_DATE]: {
    statusName: ORDER_STATUS.ACTIVE,
    events: [createEvent('Orden activa', 'La orden pasa a estado Activo')]
  },
  [ORDER_DATES.DELIVERY_DATE]: {
    statusName: ORDER_STATUS.DELIVERED,
    events: [createEvent('Orden entregada', 'La orden pasa a estado Entregado')]
  },
  [ORDER_DATES.CANCELLED_DATE]: {
    statusName: ORDER_STATUS.CANCELLED,
    events: [createEvent('Orden cancelada', 'La orden pasa a estado Cancelado')]
  }
} as const;

/**
 * Eventos base del servicio logístico
 */
export const LOGISTIC_SERVICE_HISTORY_BASE_EVENTS: TLogisticServiceHistoryBaseEvents = {
  [LOGISTIC_SERVICE_DATES.REQUEST_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.TO_CONFIRMED,
    events: [createEvent('Solicitado', 'Se solicita el servicio logístico he ingresa al estado Por confirmar')]
  },
  [LOGISTIC_SERVICE_DATES.CONFIRMED_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED,
    events: [createEvent('Confirmado', 'El servicio pasa a estado Confirmado')]
  },
  [LOGISTIC_SERVICE_DATES.IN_PROCESS_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS,
    events: [createEvent('En proceso', 'El servicio pasa a estado En proceso')]
  },
  [LOGISTIC_SERVICE_DATES.ACTIVATION_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.ACTIVE,
    events: [createEvent('Activo', 'El servicio pasa a estado Activo')]
  },
  [LOGISTIC_SERVICE_DATES.DELIVERED_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.DELIVERED,
    events: [createEvent('Entregado', 'El servicio pasa a estado Entregado')]
  },
  [LOGISTIC_SERVICE_DATES.CANCELLED_DATE]: {
    statusName: LOGISTIC_SERVICE_STATUS_NAME.CANCELLED,
    events: [createEvent('Cancelado', 'El servicio pasa a estado Cancelado')]
  }
} as const;
